import React, { useState } from 'react';
import { useSprings, animated, to as interpolate } from '@react-spring/web';
import { useDrag } from 'react-use-gesture';
import { X, Check, MoreHorizontal, Users, Calendar } from 'lucide-react';
// --- MOCK DATA ---
// Represents the event cards a user would discover.
const initialCards = [{
  id: 1,
  type: 'NBA Game',
  title: 'Lakers vs. Warriors',
  location: 'Crypto.com Arena',
  date: 'SAT, OCT 26 - 7:30 PM',
  image: 'https://placehold.co/600x800/22252A/FFF?text=Lakers\\nvs\\nWarriors',
  socialProof: '3 friends are interested'
}, {
  id: 2,
  type: 'CONCERT',
  title: 'Indie Fest 2025',
  location: 'Central Park',
  date: 'FRI, NOV 1 - 5:00 PM',
  image: 'https://placehold.co/600x800/2C2F33/FFF?text=Indie\\nFest',
  socialProof: 'Popular in your "Concert" Circle'
}, {
  id: 3,
  type: 'ART EXHIBIT',
  title: 'Modern Art Showcase',
  location: 'City Art Museum',
  date: 'ALL WEEKEND',
  image: 'https://placehold.co/600x800/1A1A1D/FFF?text=Art\\nShowcase',
  socialProof: 'Recommended for you'
}, {
  id: 4,
  type: 'FOOD FESTIVAL',
  title: 'Downtown Taco Fest',
  location: 'Grand Park',
  date: 'SAT, OCT 26 - 12:00 PM',
  image: 'https://placehold.co/600x800/3A3D42/FFF?text=Taco\\nFest',
  socialProof: '5 friends are going'
}];
// --- CARD STYLING & ANIMATION LOGIC ---
// Helper function to calculate the card's position and rotation.
const to = (i: number) => ({
  x: 0,
  y: i * -4,
  scale: 1,
  rot: -10 + Math.random() * 20,
  delay: i * 100
});
// Helper function to define the card's resting state.
const from = (_i: number) => ({
  x: 0,
  rot: 0,
  scale: 1.5,
  y: -1000
});
// Defines how the card transforms based on spring values.
const trans = (r: number, s: number) => `perspective(1500px) rotateX(30deg) rotateY(${r / 10}deg) rotateZ(${r}deg) scale(${s})`;
export const SwipeableCardStack = () => {
  // State to keep track of which cards have been "swiped".
  const [gone] = useState(() => new Set());
  // React Spring hook to manage animations for each card.
  const [props, api] = useSprings(initialCards.length, i => ({
    ...to(i),
    from: from(i)
  }));
  // Gesture hook to handle dragging and swiping.
  const bind = useDrag(({
    args: [index],
    down,
    movement: [mx, my],
    direction: [xDir],
    velocity
  }) => {
    const trigger = velocity > 0.2; // Velocity threshold to trigger a swipe.
    const dir = xDir < 0 ? -1 : 1; // -1 for left, 1 for right.
    const upDir = my < 0; // true if swiping up
    // If the user swipes with enough velocity, mark the card as "gone".
    if (!down && trigger) {
      if (upDir) {
        console.log(`Card ${index}: Details`);
        gone.add(index);
      } else {
        console.log(`Card ${index}: ${dir === 1 ? 'Saved' : 'Ignored'}`);
        gone.add(index);
      }
    }
    // Update the spring animation for the card being dragged.
    api.start(i => {
      if (index !== i) return; // Only affect the card being dragged.
      const isGone = gone.has(index);
      // Calculate the card's new position based on the gesture.
      const x = isGone ? (200 + window.innerWidth) * (upDir ? 0 : dir) : down ? mx : 0;
      const y = isGone ? -1000 : down ? my : 0;
      const rot = mx / 100 + (isGone ? dir * 10 * velocity : 0);
      const scale = down ? 1.1 : 1;
      return {
        x,
        y,
        rot,
        scale,
        delay: undefined,
        config: {
          friction: 50,
          tension: down ? 800 : isGone ? 200 : 500
        }
      };
    });
    // If all cards are gone, reset the deck after a delay.
    if (!down && gone.size === initialCards.length) {
      setTimeout(() => {
        gone.clear();
        api.start(i => to(i));
      }, 600);
    }
  });
  return <div className="flex items-center justify-center w-full overflow-hidden" data-id="element-188">
      <div className="w-full max-w-sm h-full flex flex-col items-center justify-center" data-id="element-189">
        {/* Card Stack */}
        <div className="relative w-full h-[500px] flex items-center justify-center" data-id="element-190">
          {props.map(({
          x,
          y,
          rot,
          scale
        }, i) => <animated.div key={initialCards[i].id} style={{
          x,
          y
        }} className="absolute w-full h-full will-change-transform flex items-center justify-center" data-id="element-191">
              <animated.div {...bind(i)} style={{
            transform: interpolate([rot, scale], trans)
          }} className="w-[90vw] max-w-sm h-[500px] bg-[#2C2F33] rounded-2xl shadow-2xl touch-none cursor-grab active:cursor-grabbing relative overflow-hidden" data-id="element-192">
                <img src={initialCards[i].image} alt={initialCards[i].title} className="absolute inset-0 w-full h-full object-cover opacity-30" data-id="element-193" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" data-id="element-194" />
                <div className="absolute bottom-0 left-0 p-6 text-white w-full" data-id="element-195">
                  <span className="text-sm font-bold tracking-wider text-blue-400" data-id="element-196">
                    {initialCards[i].type}
                  </span>
                  <h2 className="text-3xl font-bold mt-1" data-id="element-197">
                    {initialCards[i].title}
                  </h2>
                  <p className="text-lg text-gray-300" data-id="element-198">
                    {initialCards[i].location}
                  </p>
                  <div className="mt-4 pt-4 border-t border-white/20 flex flex-col space-y-2 text-sm" data-id="element-199">
                    <div className="flex items-center gap-2" data-id="element-200">
                      <Calendar size={16} className="opacity-70" data-id="element-201" />
                      <span data-id="element-202">{initialCards[i].date}</span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-203">
                      <Users size={16} className="opacity-70" data-id="element-204" />
                      <span data-id="element-205">{initialCards[i].socialProof}</span>
                    </div>
                  </div>
                </div>
              </animated.div>
            </animated.div>)}
        </div>
        {/* Button Controls */}
        <div className="flex items-center justify-center gap-4 mt-8" data-id="element-206">
          <button className="w-16 h-16 rounded-full bg-[#3A3D42] text-red-500 flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform" aria-label="Ignore event" data-id="element-207">
            <X size={32} data-id="element-208" />
          </button>
          <button className="w-20 h-20 rounded-full bg-[#3A3D42] text-white flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform" aria-label="View more details" data-id="element-209">
            <MoreHorizontal size={32} data-id="element-210" />
          </button>
          <button className="w-16 h-16 rounded-full bg-[#3A3D42] text-green-500 flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform" aria-label="Save event" data-id="element-211">
            <Check size={32} data-id="element-212" />
          </button>
        </div>
      </div>
    </div>;
};